import { addDays, format, parse, startOfDay } from 'date-fns';
import { CircleX } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import CalenderIcon from '@/assets/icons/calendar.svg?react';
import PillButton from '@/components/shared/pill-button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import {
  SEARCH_PARAM_ROUTE_TYPE,
  SEARCH_PARAM_SELECTED_DATE,
  SEARCH_PARAM_SORT_BY,
} from '@/utils/constants/queries';
import { calendarLocale } from '@/utils/helpers/calendar-locale';
import formatDate from '@/utils/helpers/format-date';
import { getToday } from '@/utils/helpers/get-formatted-date-for-api';

const DateFilterPopover = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const todayString = formatDate(getToday());
  const selectedDateStr = searchParams.get(SEARCH_PARAM_SELECTED_DATE) ?? todayString;

  const selectedDate = parse(selectedDateStr, 'dd MMM yyyy', getToday());
  const localedDate = format(selectedDate, 'dd MMM yyyy', { locale: calendarLocale });
  const isToday = selectedDateStr === todayString;

  const disabledDates = useMemo(() => {
    const today = startOfDay(getToday());
    const maxDate = addDays(today, 30);

    return {
      before: today,
      after: maxDate,
    };
  }, []);

  const onTogglePopover = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const onHandleClear = useCallback(() => {
    setSearchParams((prev) => {
      prev.delete(SEARCH_PARAM_SELECTED_DATE);
      prev.delete(SEARCH_PARAM_ROUTE_TYPE);

      return prev;
    });

    onTogglePopover();
  }, [setSearchParams, onTogglePopover]);

  const onSelectHandler = useCallback(
    (newlySelectedDate: Date | undefined) => {
      if (!newlySelectedDate) {
        return;
      }

      const formattedDate = format(newlySelectedDate, 'dd MMM yyyy');
      const clickedDateIsToday = formattedDate === todayString;

      setSearchParams((prev) => {
        prev.set(SEARCH_PARAM_SELECTED_DATE, formattedDate);

        if (!clickedDateIsToday) {
          if (prev.get(SEARCH_PARAM_SORT_BY) === 'deviation') {
            prev.delete(SEARCH_PARAM_SORT_BY);
          }
          prev.set(SEARCH_PARAM_ROUTE_TYPE, 'inactive');
        } else {
          prev.delete(SEARCH_PARAM_ROUTE_TYPE);
        }

        return prev;
      });

      onTogglePopover();
    },
    [todayString, setSearchParams, onTogglePopover]
  );

  return (
    <Popover open={isOpen} onOpenChange={onTogglePopover}>
      <PopoverTrigger className="cursor-pointer" asChild>
        <PillButton
          className={cn('w-max transition-all duration-300 ease-in-out flex items-center gap-0', {
            'bg-primary text-white hover:bg-primary hover:text-white': isToday,
          })}
        >
          <div className="flex gap-2">
            <CalenderIcon
              className={cn('size-5 transition-colors duration-300', {
                'fill-white': isToday,
              })}
            />
            <span>{isToday ? t('common.today') : localedDate}</span>
          </div>
          <div
            onClick={(e) => {
              e.stopPropagation();
              onHandleClear();
            }}
            className={cn(
              'cursor-pointer h-fit hover:bg-transparent hover:h-fit p-0 overflow-hidden transition-all duration-300 ease-in-out flex items-center w-5 opacity-100 ml-2 pointer-events-auto',
              { 'max-w-0 min-w-0 opacity-0 ml-0 pointer-events-none': isToday }
            )}
          >
            <CircleX fill="black" className="text-secondary size-5 shrink-0" />
          </div>
        </PillButton>
      </PopoverTrigger>
      <PopoverContent className="w-auto overflow-hidden p-0" align="start">
        <Calendar
          mode="single"
          selected={selectedDate}
          disabled={disabledDates}
          onSelect={onSelectHandler}
          locale={calendarLocale}
        />
      </PopoverContent>
    </Popover>
  );
};

export default DateFilterPopover;
